/**
 * Guide mode helpers (#436) — pure functions the guide components share.
 * Components and the API client live in `components/guides/` and
 * `lib/api/guides.ts`.
 */
import type { GuideChecklistStep } from "@nodus/ui";
import type { ColleagueGuideCell, GuideStep, GuideSummary } from "@/lib/api/guides";
import {
  currentLoginId,
  loginScopedKey,
  loginStore,
  readKey,
  writeKey,
  type KeyValueStore,
} from "@/lib/login-session";

/** The realtime event the backend sends when a member's guides changed. */
export const GUIDES_CHANGED_EVENT = "guides_changed";

/** A link that opens a guide from outside the portal: the dashboard with the
 *  guide's popup open. A guide is never a page of its own (#436); in-app
 *  openers add `?guide=` to the current page instead (`useGuideLink`). */
export function guidePath(key: string): string {
  return `/?guide=${encodeURIComponent(key)}`;
}

const STATUS_ORDER: Record<string, number> = {
  reopened: 0,
  in_progress: 1,
  not_started: 2,
  dismissed: 3,
  done: 4,
};

/** Guides that need the member first — reopened, started, new — finished ones last. */
export function sortGuides<T extends Pick<GuideSummary, "status">>(guides: T[]): T[] {
  return guides
    .map((g, i) => [g, i] as const)
    .sort(([a, ai], [b, bi]) => (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2) || ai - bi)
    .map(([g]) => g);
}

/** Office Guide search: title or summary, case-insensitive. */
export function guideMatches(guide: Pick<GuideSummary, "title" | "summary">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return guide.title.toLowerCase().includes(q) || guide.summary.toLowerCase().includes(q);
}

/**
 * The portal area a path is in — the longest matching route prefix from the
 * guides API's `areas`. `/` (the dashboard) only matches itself.
 */
export function areaForPath(pathname: string, areas: Record<string, string>): string | null {
  let best: string | null = null;
  let bestLength = -1;
  for (const [key, route] of Object.entries(areas)) {
    const hit = route === "/" ? pathname === "/" : pathname === route || pathname.startsWith(`${route}/`);
    if (hit && route.length > bestLength) {
      best = key;
      bestLength = route.length;
    }
  }
  return best;
}

/** The steps the portal can show: a block step needs a component for its block. */
export function memberSteps(steps: GuideStep[], hasBlock: (key: string) => boolean): GuideStep[] {
  return steps.filter((s) => s.content_kind !== "block" || hasBlock(s.block_key));
}

/**
 * What finishing a step's block means for the step (#436 MR 5):
 * - `tick` — an `action` step (or an `info` step whose block did its job, like
 *   queuing a new Wi-Fi password) is ticked through the normal endpoint;
 * - `refetch` — an `auto` step is detected server-side, so the guide reloads
 *   and the check runs again;
 * - `null` — already done: nothing to do.
 */
export function blockCompletion(
  step: Pick<GuideStep, "behaviour" | "done"> & { block_key?: string },
): "tick" | "refetch" | null {
  if (step.behaviour === "auto") return "refetch";
  // A quiz saved its step itself (the tick endpoint refuses it): just reload.
  if (step.block_key && SELF_COMPLETING_BLOCKS.has(step.block_key)) return "refetch";
  return step.done ? null : "tick";
}

/** Blocks that complete their step through their own endpoint (the backend's
 *  `guides.blocks.SELF_COMPLETING`). */
export const SELF_COMPLETING_BLOCKS: ReadonlySet<string> = new Set(["guide.quiz"]);

/** A step as the shared `GuideChecklist` shows it. */
export function toChecklistStep(step: GuideStep): GuideChecklistStep {
  return {
    key: step.key,
    title: step.title,
    done: step.done,
    kind: step.behaviour === "auto" ? "auto" : step.behaviour === "action" ? "action" : "manual",
    company: step.audience === "admins",
    important: step.important,
    doneBy: step.done_by,
  };
}

/** One cell of the colleagues' progress table. */
export function colleagueCellLabel(cell: ColleagueGuideCell | undefined): string {
  if (!cell || cell.total === 0) return "—";
  if (cell.completed) return "Finished";
  return `${cell.done}/${cell.total}`;
}

// --- nudges (#436 MR 6) -------------------------------------------------------------

type Audienced = Pick<GuideSummary, "audience">;

/** Defence in depth: the API never lists an admins-only guide to a member, and
 *  the nudges don't trust that alone. */
function forViewer<T extends Audienced>(guides: T[], isCompanyAdmin: boolean): T[] {
  return guides.filter((g) => g.audience !== "admins" || isCompanyAdmin);
}

const OPEN_STATUSES = new Set(["not_started", "in_progress", "reopened"]);

/** The dashboard's get-started card: guides with something left to do, most
 *  urgent first. Finished and skipped ones are out; empty = no card. */
export function unfinishedGuides<T extends GuideSummary>(guides: T[], isCompanyAdmin: boolean): T[] {
  return sortGuides(forViewer(guides, isCompanyAdmin).filter((g) => g.total > 0 && OPEN_STATUSES.has(g.status)));
}

interface NudgeData {
  guides: GuideSummary[];
  is_company_admin: boolean;
}

/**
 * The one-time banner on the current page: guides placed in its area that the
 * server still flags `banner_pending` (not dismissed there, not finished or
 * skipped). Never on the dashboard — the get-started card is the nudge there —
 * nor on pages outside the areas.
 */
export function bannerGuides(
  pathname: string,
  data: (NudgeData & { areas: Record<string, string> }) | undefined,
): GuideSummary[] {
  if (!data) return [];
  const area = areaForPath(pathname, data.areas);
  if (!area || area === "dashboard") return [];
  return sortGuides(
    forViewer(data.guides, data.is_company_admin).filter(
      (g) => g.banner_pending && g.areas.includes(area) && g.total > 0 && OPEN_STATUSES.has(g.status),
    ),
  );
}

/** The chips in an area page's header: every guide pinned to the area, open ones first. */
export function sectionGuides(area: string, data: NudgeData | undefined): GuideSummary[] {
  if (!data) return [];
  return sortGuides(forViewer(data.guides, data.is_company_admin).filter((g) => g.total > 0 && g.areas.includes(area)));
}

// --- login popup ----------------------------------------------------------------------

/**
 * The guides the login popup lists: every unfinished one (not started, in
 * progress, or finished but reopened by a new important step), most urgent
 * first. Finished and skipped guides stay out, and so do guides the office
 * switched off for the popup. A missing `popup` (an older backend) counts as on.
 */
export function popupGuides<T extends GuideSummary>(guides: T[], isCompanyAdmin: boolean): T[] {
  return unfinishedGuides(guides, isCompanyAdmin).filter((g) => g.popup !== false);
}

/** Which login last had its guide popup — cleared at logout with the login id. */
export const GUIDE_POPUP_SEEN_KEY = loginScopedKey("guide_popup_login");

/**
 * Once per login: the guides to pop up now, or `[]`. The first call in a login
 * uses the popup up — whether or not anything qualified — so reloads, other
 * tabs and later changes in the same login never pop it up again; the next
 * login does.
 */
export function takeGuidePopup(
  data: Pick<NudgeData, "guides" | "is_company_admin">,
  store: KeyValueStore = loginStore(),
): GuideSummary[] {
  const login = currentLoginId(store);
  if (readKey(store, GUIDE_POPUP_SEEN_KEY) === login) return [];
  writeKey(store, GUIDE_POPUP_SEEN_KEY, login);
  return popupGuides(data.guides, data.is_company_admin);
}

/** Announcements that own the whole screen; the login popup never covers them. */
const SCREEN_TAKING = new Set(["fullscreen", "fullscreen_takeover"]);

export interface GuidePopupGate {
  /** The guides list came from a fetch after the popup mounted — never a
   *  previous session's cached copy. */
  guidesFetchedAfterMount: boolean;
  /** The announcements and emergency state are known (fetched or failed). */
  announcementsLoaded: boolean;
  emergencyLoaded: boolean;
  /** What the announcements host shows (takeover suppression and dismissals applied). */
  visibleAnnouncements: { display_type: string }[];
  emergencyActive: boolean;
  /** A guide is open in the guide modal (`?guide=` in the URL). */
  guideOpen?: boolean;
}

/**
 * Whether the login popup may decide now. It waits — without using up the
 * login — while a fullscreen announcement, a takeover or an emergency is
 * showing (a setup-guide list must never cover or steal focus from those),
 * until it knows whether one is, and while a guide is open in the guide modal
 * (a `?guide=` link opens straight into it).
 */
export function guidePopupReady(gate: GuidePopupGate): boolean {
  if (!gate.guidesFetchedAfterMount || !gate.announcementsLoaded || !gate.emergencyLoaded) return false;
  if (screenTaken(gate.visibleAnnouncements, gate.emergencyActive)) return false;
  return !gate.guideOpen;
}

/** A fullscreen announcement, a takeover or an emergency is showing: an open
 *  popup steps aside (and a pending one waits). */
export function screenTaken(visibleAnnouncements: { display_type: string }[], emergencyActive: boolean): boolean {
  return emergencyActive || visibleAnnouncements.some((a) => SCREEN_TAKING.has(a.display_type));
}
